import {
  getEditorRequestConfig,
  jsonToGeojson,
  flyToOptions,
  debounce
} from "@/utils";
import { trafficCountsLayersConfig } from "../configs";

export class BaseController {
  constructor(parent) {
    this.parent = parent;
    this.mapgl = parent.mapgl;
    this.$store = parent.$store;
    this.handlers = {};
  }

  // toggling base layers
  toggleOn(ids) {
    ["nodes", "links"].forEach(type => this.toggleOnBaselayer(type, ids));
  }
  toggleOff(ids) {
    ["nodes", "links"].forEach(type => this.toggleOffBaselayer(type, ids));
  }

  // toggle one base layer
  toggleOnBaselayer(type, ids) {
    const id = ids[type];

    const loadModelLayer = async () => {
      const zoom = this.mapgl.getZoom();
      const config = getEditorRequestConfig(this.parent);
      if (type === "nodes") {
        config.include = {
          from_links: { only: ["id"] }
        };
      }
      // modify config for gcp
      if (type === "general_count_points") {
        config.only = [
          "id",
          "geom",
          "no",
          "detalization_type",
          "interval_type"
        ];
      }
      // modify config for cp
      if (type === "count_points") {
        config.only = ["id", "geom", "name"];
        config.include = {
          general_count_point: {
            only: ["id", "geom", "detalization_type", "interval_type"],
            include: {
              link: {
                only: ["id", "geom"]
              }
            }
          },
          link: {
            only: ["id", "geom"]
          }
        };
      }

      try {
        const url = `objectInfo/${id}?config=${JSON.stringify(
          config
        )}&zoom=${zoom}`;
        const { data } = await this.$store.dispatch("REQUEST", {
          url
        });
        const geojson = jsonToGeojson(Object.keys(data).map(id => data[id]));
        if (!this.mapgl.getLayer(id)) {
          const options = {
            id,
            source: {
              type: "geojson",
              data: geojson
            },
            ...trafficCountsLayersConfig[type]
          };

          if (this.mapgl.getLayer(ids["nodes"]))
            this.mapgl.addLayer(options, ids["nodes"]);
          else if (this.mapgl.getLayer("gl-draw-polygon-fill-inactive.cold"))
            this.mapgl.addLayer(options, "gl-draw-polygon-fill-inactive.cold");
          else this.mapgl.addLayer(options);

          if (type === "general_count_points" || type === "count_points") {
            this.mapgl.addLayer({
              id: `${id}_label`,
              source: id,
              ...trafficCountsLayersConfig[`${type}_label`]
            });
          }
        } else {
          this.mapgl.getSource(id).setData(geojson);
        }
      } catch (error) {
        console.log(error);
      }
    };

    this.handlers[id] = debounce(loadModelLayer, 200);
    this.handlers[id]();
    this.mapgl.on("moveend", this.handlers[id]);
  }
  toggleOffBaselayer(type, ids) {
    const id = ids[type];

    if (this.mapgl.getLayer(id)) this.mapgl.removeLayer(id);

    if (type === "general_count_points" || type === "count_points") {
      if (this.mapgl.getLayer(`${id}_label`))
        this.mapgl.removeLayer(`${id}_label`);
    }
    if (this.mapgl.getSource(id)) this.mapgl.removeSource(id);

    this.mapgl.off("moveend", this.handlers[id]);
    delete this.handlers[id];
  }

  // set transport systems to store
  async setTransportSystems(ids) {
    const id = ids["sys_set_measures"];

    try {
      const url = `objectInfo/${id}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      this.$store.commit("SET_TC_TRANSPORT_SYSTEMS", Object.values(data));
    } catch (error) {
      console.log(error);
    }
  }

  // set transport profiles to store
  async setProfiles(ids) {
    const id = ids["coefficient_profiles"];
    const config = {
      include: {
        coefficients: {}
      }
    };

    try {
      const url = `objectInfo/${id}?config=${JSON.stringify(config)}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      const profiles = Object.values(data);
      this.$store.commit("SET_TC_PROFILES", profiles);
      if (profiles.length)
        this.$store.commit("SET_TC_FILL_PROP", {
          name: "profile",
          value: profiles[0].id
        });
    } catch (error) {
      console.log(error);
    }
  }

  // fly to model
  async flyToModel(id) {
    if (!this.$store.state.map.extent[id]) {
      const { data } = await this.$store.dispatch("REQUEST", {
        url: `geom_extent/${id}?`
      });
      const extent = data;
      this.$store.commit("SET_LAYER_EXTENT", { id, extent });
    }

    const extent = this.$store.state.map.extent[id];
    const { center, zoom } = this.mapgl.cameraForBounds(
      [extent.slice(0, 2), extent.slice(2)],
      { padding: { top: 10, bottom: 10, left: 10, right: 10 } }
    );
    this.mapgl.flyTo({
      center,
      zoom,
      ...flyToOptions
    });
  }
}
